import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Alert,
  Tag,
  Typography,
  Divider,
  Steps,
  Input,
  Space,
  Spin,
  Modal,
} from 'antd';
import {
  SafetyCertificateOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  MobileOutlined,
  QrcodeOutlined,
  KeyOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { mfaStatus, mfaSetup, mfaConfirmSetup, mfaDisable } from '../../../API/auth';
import './styles.css';

const { Title, Text, Paragraph } = Typography;

// Step indices for the setup wizard
const SETUP_IDLE = -1;   // not in setup flow
const SETUP_QR   = 0;    // showing QR code
const SETUP_CODE = 1;    // entering verification code
const SETUP_DONE = 2;    // success

function MFASettings() {
  const [statusLoading, setStatusLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled]       = useState(false);

  // Setup flow state
  const [setupStep, setSetupStep]     = useState(SETUP_IDLE);
  const [setupLoading, setSetupLoading] = useState(false);
  const [qrCode, setQrCode]           = useState('');      // base64 PNG from backend
  const [secret, setSecret]           = useState('');      // manual entry key
  const [setupCode, setSetupCode]     = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [setupError, setSetupError]   = useState('');

  // Disable flow state
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disableCode, setDisableCode]           = useState('');
  const [disableLoading, setDisableLoading]     = useState(false);
  const [disableError, setDisableError]         = useState('');

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await mfaStatus();
      setMfaEnabled(res.data?.mfa_enabled ?? false);
    } catch {
      // silently fail — user sees status as disabled
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // ── Setup Flow ──────────────────────────────────────────────────────────────

  const handleStartSetup = async () => {
    setSetupLoading(true);
    setSetupError('');
    try {
      const res = await mfaSetup();
      if (res.status !== 200) throw new Error(res.data?.error || 'Setup failed.');
      setQrCode(res.data.qr_code);   // base64 data URI: "data:image/png;base64,..."
      setSecret(res.data.secret);    // e.g. "JBSWY3DPEHPK3PXP"
      setSetupStep(SETUP_QR);
    } catch (err) {
      setSetupError(err.response?.data?.error || 'Could not start MFA setup. Please try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleConfirmSetup = async () => {
    if (!setupCode || setupCode.length !== 6) {
      setSetupError('Enter the 6-digit code from Google Authenticator.');
      return;
    }
    setSetupLoading(true);
    setSetupError('');
    try {
      const res = await mfaConfirmSetup(setupCode);
      if (res.status !== 200) throw new Error(res.data?.error || 'Verification failed.');
      setBackupCodes(res.data.backup_codes || []);
      setSetupStep(SETUP_DONE);
      setMfaEnabled(true);
    } catch (err) {
      setSetupError(err.response?.data?.error || 'Invalid code. Make sure your phone clock is synced and try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleCancelSetup = () => {
    setSetupStep(SETUP_IDLE);
    setQrCode('');
    setSecret('');
    setSetupCode('');
    setSetupError('');
    setBackupCodes([]);
  };

  const handleCodeChange = (e, setter) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setter(val);
    setSetupError('');
  };

  // ── Disable Flow ─────────────────────────────────────────────────────────────

  const handleDisable = async () => {
    if (!disableCode || disableCode.length !== 6) {
      setDisableError('Enter your current 6-digit authentication code.');
      return;
    }
    setDisableLoading(true);
    setDisableError('');
    try {
      const res = await mfaDisable(disableCode);
      if (res.status !== 200) throw new Error(res.data?.error || 'Disable failed.');
      setMfaEnabled(false);
      setDisableModalOpen(false);
      setDisableCode('');
      setSetupStep(SETUP_IDLE);
    } catch (err) {
      setDisableError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setDisableLoading(false);
    }
  };

  const handleCloseDisableModal = () => {
    setDisableModalOpen(false);
    setDisableCode('');
    setDisableError('');
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (statusLoading) {
    return (
      <div className="mfa-loading">
        <Spin size="large" />
        <Text type="secondary" style={{ marginTop: 12 }}>Loading MFA status…</Text>
      </div>
    );
  }

  return (
    <div className="mfa-page">
      <div className="mfa-page-header">
        <Title level={3} style={{ margin: 0 }}>
          <SafetyCertificateOutlined style={{ marginRight: 10, color: '#1890ff' }} />
          Two-Factor Authentication
        </Title>
        <Text type="secondary">
          Secure your account with Google Authenticator. Once enabled, you will need to enter a
          6-digit code from the app every time you sign in.
        </Text>
      </div>

      {/* ── Status Card ── */}
      <Card className="mfa-status-card">
        <div className="mfa-status-row">
          <div className="mfa-status-info">
            <div className="mfa-status-label">
              {mfaEnabled ? (
                <>
                  <CheckCircleFilled style={{ color: '#52c41a', marginRight: 8, fontSize: 18 }} />
                  <Text strong style={{ fontSize: 16 }}>MFA is Active</Text>
                  <Tag color="success" style={{ marginLeft: 12 }}>Enabled</Tag>
                </>
              ) : (
                <>
                  <CloseCircleFilled style={{ color: '#ff4d4f', marginRight: 8, fontSize: 18 }} />
                  <Text strong style={{ fontSize: 16 }}>MFA is Not Configured</Text>
                  <Tag color="error" style={{ marginLeft: 12 }}>Disabled</Tag>
                </>
              )}
            </div>
            <Text type="secondary" style={{ marginTop: 4, display: 'block' }}>
              {mfaEnabled
                ? 'Your account is protected. Each login requires a code from Google Authenticator.'
                : 'Your account is not protected by MFA. Set it up below to improve security.'}
            </Text>
          </div>

          {mfaEnabled && setupStep === SETUP_IDLE && (
            <Button
              danger
              icon={<CloseCircleFilled />}
              onClick={() => setDisableModalOpen(true)}
            >
              Disable MFA
            </Button>
          )}
        </div>
      </Card>

      {/* ── Setup Flow ── */}
      {!mfaEnabled && (
        <Card className="mfa-setup-card">
          {setupStep === SETUP_IDLE && (
            <div className="mfa-start-section">
              <Title level={4} style={{ margin: '0 0 8px' }}>
                <MobileOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                Set Up Google Authenticator
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 20 }}>
                You will need the{' '}
                <strong>Google Authenticator</strong> app installed on your phone before starting.
                Available on{' '}
                <a
                  href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  iOS
                </a>{' '}
                and{' '}
                <a
                  href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Android
                </a>
                .
              </Paragraph>

              <div className="mfa-steps-preview">
                <div className="mfa-step-item">
                  <div className="mfa-step-num">1</div>
                  <Text>Install Google Authenticator on your phone</Text>
                </div>
                <div className="mfa-step-item">
                  <div className="mfa-step-num">2</div>
                  <Text>Scan the QR code with the app</Text>
                </div>
                <div className="mfa-step-item">
                  <div className="mfa-step-num">3</div>
                  <Text>Enter the 6-digit code to confirm</Text>
                </div>
              </div>

              {setupError && (
                <Alert
                  message={setupError}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                  onClose={() => setSetupError('')}
                  closable
                />
              )}

              <Button
                type="primary"
                size="large"
                icon={<QrcodeOutlined />}
                loading={setupLoading}
                onClick={handleStartSetup}
              >
                Begin Setup
              </Button>
            </div>
          )}

          {(setupStep === SETUP_QR || setupStep === SETUP_CODE) && (
            <>
              <Steps
                current={setupStep}
                style={{ marginBottom: 32 }}
                items={[
                  { title: 'Scan QR Code', icon: <QrcodeOutlined /> },
                  { title: 'Verify Code',  icon: <KeyOutlined /> },
                ]}
              />

              {setupStep === SETUP_QR && (
                <div className="mfa-qr-section">
                  <Title level={5}>Scan with Google Authenticator</Title>
                  <Paragraph type="secondary">
                    Open Google Authenticator → tap <strong>+</strong> → select{' '}
                    <strong>Scan a QR code</strong>, then point your camera at the code below.
                  </Paragraph>

                  <div className="mfa-qr-wrapper">
                    {qrCode ? (
                      <img src={qrCode} alt="MFA QR Code" className="mfa-qr-image" />
                    ) : (
                      <Spin />
                    )}
                  </div>

                  <Divider plain>Can't scan? Enter manually</Divider>

                  <div className="mfa-manual-key">
                    <Text type="secondary" style={{ fontSize: 12 }}>Account name: <strong>Arett Contract Portal</strong></Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>Key:</Text>
                    <div className="mfa-secret-box">
                      <Text code style={{ fontSize: 15, letterSpacing: 2 }}>
                        {secret.match(/.{1,4}/g)?.join(' ') || secret}
                      </Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      In the app: tap <strong>+</strong> → <strong>Enter a setup key</strong> → paste the key above.
                    </Text>
                  </div>

                  <div className="mfa-action-row">
                    <Button onClick={handleCancelSetup}>Cancel</Button>
                    <Button type="primary" onClick={() => { setSetupStep(SETUP_CODE); setSetupError(''); }}>
                      I've Scanned It — Next
                    </Button>
                  </div>
                </div>
              )}

              {setupStep === SETUP_CODE && (
                <div className="mfa-verify-section">
                  <Title level={5}>Enter the Verification Code</Title>
                  <Paragraph type="secondary">
                    Open Google Authenticator and enter the 6-digit code shown for{' '}
                    <strong>Arett Contract Portal</strong> to confirm your setup.
                  </Paragraph>

                  {setupError && (
                    <Alert
                      message={setupError}
                      type="error"
                      showIcon
                      closable
                      style={{ marginBottom: 16 }}
                      onClose={() => setSetupError('')}
                    />
                  )}

                  <div className="mfa-code-field">
                    <Input
                      value={setupCode}
                      onChange={(e) => handleCodeChange(e, setSetupCode)}
                      onPressEnter={handleConfirmSetup}
                      placeholder="000000"
                      maxLength={6}
                      size="large"
                      className="mfa-code-input"
                      autoFocus
                      inputMode="numeric"
                    />
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                      The code changes every 30 seconds.
                    </Text>
                  </div>

                  <div className="mfa-action-row">
                    <Button onClick={() => { setSetupStep(SETUP_QR); setSetupError(''); }}>
                      Back
                    </Button>
                    <Button
                      type="primary"
                      loading={setupLoading}
                      onClick={handleConfirmSetup}
                      disabled={setupCode.length !== 6}
                    >
                      Enable MFA
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {setupStep === SETUP_DONE && (
            <div className="mfa-success-section">
              <CheckCircleFilled className="mfa-success-icon" />
              <Title level={4} style={{ color: '#52c41a', margin: '12px 0 8px' }}>
                MFA Successfully Enabled!
              </Title>
              <Paragraph type="secondary">
                Your account is now protected. You will be asked for a code from Google
                Authenticator each time you sign in.
              </Paragraph>

              {backupCodes.length > 0 && (
                <div className="mfa-backup-section">
                  <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    message="Save Your Backup Codes"
                    description="Store these codes somewhere safe. Each can be used once to sign in if you lose access to your phone."
                    style={{ marginBottom: 16 }}
                  />
                  <div className="mfa-backup-grid">
                    {backupCodes.map((c, i) => (
                      <Text key={i} code className="mfa-backup-code">{c}</Text>
                    ))}
                  </div>
                </div>
              )}

              <Button type="primary" onClick={handleCancelSetup} style={{ marginTop: 16 }}>
                Done
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* ── Info Card when MFA is already active ── */}
      {mfaEnabled && setupStep === SETUP_IDLE && (
        <Card className="mfa-info-card">
          <Title level={5} style={{ marginBottom: 12 }}>
            <SafetyCertificateOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            How MFA Works
          </Title>
          <div className="mfa-info-list">
            <div className="mfa-info-item">
              <CheckCircleFilled style={{ color: '#52c41a' }} />
              <Text>On every login, after entering your password, you will be prompted for a 6-digit code.</Text>
            </div>
            <div className="mfa-info-item">
              <CheckCircleFilled style={{ color: '#52c41a' }} />
              <Text>Open Google Authenticator on your phone to retrieve the current code.</Text>
            </div>
            <div className="mfa-info-item">
              <CheckCircleFilled style={{ color: '#52c41a' }} />
              <Text>Codes are time-based and refresh every 30 seconds.</Text>
            </div>
            <div className="mfa-info-item">
              <WarningOutlined style={{ color: '#faad14' }} />
              <Text>If you lose access to your authenticator app, use a backup code to sign in, then reconfigure MFA.</Text>
            </div>
          </div>
        </Card>
      )}

      {/* ── Disable MFA Modal ── */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            Disable Two-Factor Authentication
          </Space>
        }
        open={disableModalOpen}
        onCancel={handleCloseDisableModal}
        footer={null}
        centered
        destroyOnClose
      >
        <Paragraph type="secondary" style={{ marginBottom: 20 }}>
          This will remove MFA protection from your account. You will only need your password to
          sign in. Enter your current Google Authenticator code to confirm.
        </Paragraph>

        {disableError && (
          <Alert
            message={disableError}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            onClose={() => setDisableError('')}
          />
        )}

        <div className="mfa-code-field" style={{ marginBottom: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Current Authentication Code
          </Text>
          <Input
            value={disableCode}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setDisableCode(val);
              if (disableError) setDisableError('');
            }}
            onPressEnter={handleDisable}
            placeholder="000000"
            maxLength={6}
            size="large"
            className="mfa-code-input"
            autoFocus
            inputMode="numeric"
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleCloseDisableModal}>Cancel</Button>
          <Button
            danger
            type="primary"
            loading={disableLoading}
            onClick={handleDisable}
            disabled={disableCode.length !== 6}
          >
            Yes, Disable MFA
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default MFASettings;
